import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import styles from "./Profile.module.css";
import Input from "../../../../components/Input/Input";
import Textarea from "../../../../components/Textarea/Textarea";
import FileUpload from "../../../../components/FileUpload/FileUpload";
import ProfileCompletion from "../../../../components/ProfileCompletion/ProfileCompletion";
import { useAuth } from "../../../../context/AuthContext";
import { API_ENDPOINTS } from "../../../../api/endpoints";
import apiClient from "../../../../api/apiClient";
import { toast } from "react-toastify";
import TopLabelCustomSelectBox from "../../../../components/TopLabelReactSelect/TopLabelReactSelect";
import Loader from "../../../../components/Loader/Loader";
import { useCountries } from "../../../../hooks/useCountries";

const InsituteProfile = () => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    control,
  } = useForm();
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fileUploadedKey, setFileUploadedKey] = React.useState(Date.now());
  const { userdata } = useAuth();
  const [instituteProfile, setInstituteProfile] = useState({});
  const [stateList, setStateList] = useState([]);
  const [isEditable, setIsEdiatble] = useState(false);
  const [organizationList, setOrganizationList] = useState([]);
  const [institutionTypeList, setInstitutionTypeList] = useState([]);
  const { countries, loading: countriesLoading } = useCountries();

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);

      const res = await apiClient({
        url: `${API_ENDPOINTS.VIEW_INSTITUTION_PROFILE}/${userdata.id}`,
        method: "get",
        withAuth: true,
      });
      const apiData = res?.data?.data || res?.data;
      const profile = apiData.profileData[0];
      setInstituteProfile(profile);
      // Prefill form with data received from API
      reset({
        ...profile,
        organization: profile.organization,
        institutionType: profile.institutionType,
        email: userdata.email,
        contact: userdata.contact_no,
        country: profile ? profile.country : "",
        state: profile ? profile.state : "",
        estYear: profile ? profile.estYear : "",
        logo: profile ? profile.logo : "",
        brochure: profile ? profile.brochure : "",
        phdCount: profile ? profile.phdCount : 0,
      });
      setFileUploadedKey(Date.now());
    } catch (error) {
      console.error("Profile GET Error:", error);
      if (error.status === 404) {
        toast.warning("Profile is not created yet.");
      } else {
        toast.error(
          error?.response?.data?.message
            ? error.response.data.message
            : "Unable to load profile."
        );
      }
    }

    setLoading(false);
  };


  // Fetch states AFTER profile is loaded
  useEffect(() => {
    if (instituteProfile?.country) {
      fetchStates(instituteProfile.country);
    }
  }, [instituteProfile?.country]);

  const selectedCountry = watch("country");

  useEffect(() => {
    if (selectedCountry) {
      fetchStates(selectedCountry);
    }
  }, [selectedCountry]);

  const fetchStates = async (countryId) => {
    try {
      const res = await apiClient({
        url: `${API_ENDPOINTS.GET_STATES}/${countryId}/states`,
        method: "get",
        withAuth: true,
      });

      setStateList(
        res?.data?.states?.map((item) => ({
          label: item.name,
          value: item.iso2,
        }))
      );
    } catch (error) {
      console.error("State fetch error:", error);
    }
  };

  /** country state logic ends */

  const onSubmit = async (data) => {
    if (!isEditable) return;
    setLoading(true);

    const selectedOrg = organizationList.find(
      (org) => org.value === data.organization
    );
  
    if (selectedOrg) {
      data.organization = selectedOrg.key;
    }
  
    const formData = new FormData();

    // 👉 Append NON-FILE fields
    Object.keys(data).forEach((key) => {
      if (!(data[key] instanceof FileList)) {
        formData.append(key, data[key]);
      }
    });

    // 👉 Append FILE fields
    Object.keys(data).forEach((key) => {
      if (data[key] instanceof FileList && data[key].length > 0) {
        formData.append(key, data[key][0]);
      }
    });

    formData.append("user_id", userdata.id);
    formData.append("profile_status", profileCompletion);

    try {
      const res = await apiClient({
        url: API_ENDPOINTS.UPDATE_INSTITUTION_PROFILE,
        method: "post",
        data: formData,
        withAuth: true,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Profile updated successfully!", {
        position: "top-center",
        autoClose: 2000,
        pauseOnHover: true,
        draggable: true,
      });
      const profile = res?.data?.data || res?.data;
      
      const orgOption = organizationList.find(
        (org) => org.key === profile.organization
      );
      
      const instTypeOption = institutionTypeList.find(
        (type) => type.label === profile.institutionType
      );

      setInstituteProfile(profile);
      
      
      // Prefill form with data received from API
      reset({
        ...profile,
        organization: orgOption ? orgOption.value : null,
        institutionType: instTypeOption ? instTypeOption.value : null,
        email: userdata.email,
        contact: userdata.contact_no,
        country: profile.country || null,
        state: profile.state || null,
        estYear: profile.estYear || null,
        logo: profile.logo,
        brochure: profile.brochure,
        phdCount: profile.phdCount ?? 0,
      });
      setFileUploadedKey(Date.now());
      setIsEdiatble(false);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Failed to update profile. Please try again.";

      toast.error(message, {
        position: "top-center",
        autoClose: 2000,
        pauseOnHover: true,
        draggable: true,
      });

      console.error("Upload Error:", error);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const res = await apiClient({
        url: API_ENDPOINTS.GET_ORGANIZATIONS,
        method: "get",
        withAuth: true,
      });

      setOrganizationList(
        res.data?.organizations?.map((item) => ({
          label: item.name,
          value: item.id,
          key: item.value
        }))
      );
    } catch (error) {
      console.error("Organization fetch error", error);
    }
  };
  useEffect(() => {
    if (!organizationList.length || !instituteProfile?.organization) return;
  
    const orgOption = organizationList.find(
      (org) => org.key === instituteProfile.organization
    );
  
    if (orgOption) {
      reset((prev) => ({
        ...prev,
        organization: orgOption.value,
      }));
    }
  }, [organizationList, instituteProfile]);
  
  const selectedOrganization = watch("organization");

  useEffect(() => {
    if (selectedOrganization) {
      fetchInstitutionTypes(selectedOrganization);
    } else {
      setInstitutionTypeList([]);
    }
  }, [selectedOrganization]);

  const fetchInstitutionTypes = async (organizationId) => {
    try {
      const res = await apiClient({
        url: `${API_ENDPOINTS.GET_INSTITUTION_TYPE}/${organizationId}`,
        method: "get",
        withAuth: true,
      });

      setInstitutionTypeList(
        res.data?.organizations?.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    } catch (error) {
      console.error("Institution Type fetch error", error);
    }
  };
   
  useEffect(() => {
    if (!institutionTypeList.length || !instituteProfile?.institutionType) return;
  
    const typeOption = institutionTypeList.find(
      (t) => t.label === instituteProfile.institutionType
    );
  
    if (typeOption) {
      reset((prev) => ({
        ...prev,
        institutionType: typeOption.value,
      }));
    }
  }, [institutionTypeList, instituteProfile]);
  

  return (
    <>
      {loading && <Loader fullscreen={true} />}

      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <h1 className={styles.title}>
          <span className={styles.blue}>Institution</span>{" "}
          <span className={styles.orange}> Membership Form</span>
        </h1>
        <ProfileCompletion
          watch={watch}
          exclude={[]}
          // exclude={["address"]}
          onChange={(value) => setProfileCompletion(value)}
          setIsEdiatble={setIsEdiatble}
          isEditable={isEditable}
        />
        {/* ------------ Section 1 ------------ */}
        <h2 className={styles.sectionTitle}>
          Part 1 — <span className={styles.blue}> Institution Information</span>
          <span className={styles.orange}> & Accreditations</span>
        </h2>
        <p className={styles.note}>
          <strong>Note:</strong> Part1 - all fields are mandatory (
          <span className={styles.mandate}>*</span>)
        </p>
        <div className={styles.grid3}>
          <Input
            label="Institutional Email ID"
            name="email"
            register={register}
            errors={errors}
            validation={{ required: "Email required", pattern: /^\S+@\S+$/ }}
            value={userdata.email}
            disabled={true}
          />
          <Input
            label="Official Contact Number"
            name="contact"
            register={register}
            errors={errors}
            validation={{ required: "Phone required" }}
            value={userdata.contact_no}
            disabled={true}
          />
          <Input
            label="Institution Name"
            name="institution"
            register={register}
            errors={errors}
            validation={{
              required: "Institution name is required",
            }}
            disabled={!isEditable}
          />

          <Input
            label="Full Address"
            name="address"
            register={register}
            errors={errors}
            validation={{
              required: "Full Address is required",
            }}
            disabled={!isEditable}
          />

          <TopLabelCustomSelectBox
            label="Organization"
            name="organization"
            errors={errors}
            placeholder="Select"
            options={organizationList}
            control={control}
            validation={{
              required: "Type of Organization is required",
            }}
            errorMessage={errors.organization?.message}
            disabled={!isEditable}
          />
          <TopLabelCustomSelectBox
            label="Type of Institution"
            name="institutionType"
            errors={errors}
            placeholder={
              selectedOrganization
                ? "Select Institution"
                : "Select"
            }
            options={institutionTypeList}
            control={control}
            validation={{
              required: "Type of Institution is required",
            }}
            errorMessage={errors.institutionType?.message}
            disabled={!isEditable || !selectedOrganization}
          />
          <Input
            label="Year of Establishment"
            name="estYear"
            register={register}
            errors={errors}
            year
            validation={{
              required: "Year of Establishment is required",
              maxLength: {
                value: 4,
                message: "Year must be 4 digits",
              },
              minLength: {
                value: 4,
                message: "Year must be 4 digits",
              },
            }}
            disabled={!isEditable}
          />
          <TopLabelCustomSelectBox
            label="Country"
            name="country"
            control={control}
            errors={errors}
            options={countries}
            validation={{ required: "Country is required" }}
            errorMessage={errors.country?.message}
            disabled={!isEditable}
          />

          <TopLabelCustomSelectBox
            label="State"
            name="state"
            control={control}
            errors={errors}
            options={stateList}
            validation={{ required: "State is required" }}
            errorMessage={errors.state?.message}
            disabled={!isEditable}
          />

          <Input
            label="City"
            name="city"
            register={register}
            errors={errors}
            validation={{
              required: "City is required",
            }}
            disabled={!isEditable}
          />

          <Input
            label="Website URL"
            name="website"
            register={register}
            errors={errors}
            validation={{
              required: "Website URL is required",
              pattern: {
                value: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/,
                message: "Please enter a valid website URL",
              },
            }}
            disabled={!isEditable}
          />
        </div>
        <div className={styles.grid}>
          <Textarea
            label="About the Institution"
            name="about"
            placeholder="Write about the institution"
            register={register}
            errors={errors}
            validation={{
              required: "This field is required",
              minLength: { value: 20, message: "Minimum 20 characters" },
            }}
            disabled={!isEditable}
          />

          <FileUpload
            label="Upload Institution Logo"
            name="logo"
            errors={errors}
            allowedTypes={["jpg", "png", "jpeg"]}
            maxSizeMB={10}
            key={`logo-${fileUploadedKey}`}
            errorMessage={errors?.logo?.message}
            register={(field) =>
              register(field, {
                validate: (v) => {
                  if (
                    !(instituteProfile && instituteProfile.logo) &&
                    (!v || v.length === 0)
                  ) {
                    return "Upload Institution Logo is required";
                  }
                  return true;
                },
              })
            }
            previewUrl={instituteProfile && instituteProfile.logo}
            disabled={!isEditable}
          />

          <FileUpload
            label="Upload Institution Profile / Brochure"
            name="brochure"
            errors={errors}
            required
            allowedTypes={["pdf", "jpg", "png", "jpeg"]}
            maxSizeMB={10}
            key={`brochure-${fileUploadedKey}`}
            errorMessage={errors?.brochure?.message}
            register={(field) =>
              register(field, {
                validate: (v) => {
                  if (
                    !(instituteProfile && instituteProfile.brochure) &&
                    (!v || v.length === 0)
                  ) {
                    return "Upload Institution Profile/Brochure is required";
                  }
                  return true;
                },
              })
            }
            previewUrl={instituteProfile && instituteProfile.brochure}
            disabled={!isEditable}
          />
        </div>
        <h4 className={styles.subheading}>
          <span className={styles.blue}> Accreditations </span>
          <span className={styles.orange}>& Recognitions </span>
        </h4>
        <div className={styles.grid}>
          <Input
            label="NAAC Accreditation (Grade, Cycle, Year)"
            name="naacAccreditation"
            register={register}
            errors={errors}
            required
            disabled={!isEditable}
          />
          <Input
            label="NBA Accreditation (Programs + Validity)"
            name="NBAAccreditation"
            register={register}
            errors={errors}
            required
            disabled={!isEditable}
          />
          <Input
            label="NIRF Rankings"
            name="typeAnswer"
            register={register}
            errors={errors}
            required
            disabled={!isEditable}
          />
        </div>{" "}
        <div className={styles.grid}>
          <FileUpload
            label="NAAC Certificate Upload"
            name="NAACCertificateUpload"
            register={register}
            errors={errors}
            required
            allowedTypes={["pdf", "jpg", "png", "jpeg"]}
            maxSizeMB={10}
            onFileSelect={(file) => console.log(file)}
            key={`NAACCertificate-${fileUploadedKey}`}
            previewUrl={
              instituteProfile && instituteProfile.NAACCertificateUpload
            }
            disabled={!isEditable}
          />
          <FileUpload
            label="NBA Certificate Upload"
            name="NBACertificateUpload"
            register={register}
            errors={errors}
            required
            maxSizeMB={10}
            onFileSelect={(file) => console.log(file)}
            key={`NBACertificate-${fileUploadedKey}`}
            previewUrl={
              instituteProfile && instituteProfile.NBACertificateUpload
            }
            disabled={!isEditable}
          />
          <FileUpload
            label="National / International Awards"
            name="NationalInternationalAwards"
            register={register}
            errors={errors}
            required
            allowedTypes={["pdf", "jpg", "jpeg"]}
            maxSizeMB={10}
            onFileSelect={(file) => console.log(file)}
            key={`NationalInternational-${fileUploadedKey}`}
            previewUrl={
              instituteProfile && instituteProfile.NationalInternationalAwards
            }
            disabled={!isEditable}
          />
          <FileUpload
            label="Upload Additional Documents"
            name="UploadAdditionalDocuments"
            register={register}
            errors={errors}
            required
            allowedTypes={["pdf", "jpg", "png", "jpeg"]}
            maxSizeMB={10}
            onFileSelect={(file) => console.log(file)}
            key={`UploadAdditional-${fileUploadedKey}`}
            previewUrl={
              instituteProfile && instituteProfile.UploadAdditionalDocuments
            }
            disabled={!isEditable}
          />
        </div>
        {/* ------------ Section 2 ------------ */}
        <h2 className={`${styles.sectionTitle} ${styles.topBorder}`}>
          Part 2 — <span className={styles.blue}>CONSORTIUM COORDINATOR</span>
          <span className={styles.orange}>
            {" "}
            / SPOC + RESEARCH & INNOVATION DETAILS
          </span>
        </h2>
        <h4 className={styles.subheading}>
          A. <span className={styles.blue}> CONSORTIUM COORDINATOR </span>
          <span className={styles.orange}>/ SPOC DETAILS</span>
        </h4>
        <div className={styles.grid}>
          <Input
            label="Name"
            name="coordName"
            register={register}
            errors={errors}
            required
            disabled={!isEditable}
          />
          <Input
            label="Designation"
            name="coordDesignation"
            register={register}
            errors={errors}
            required
            disabled={!isEditable}
          />
          <Input
            label="Department"
            name="coordDepartment"
            register={register}
            errors={errors}
            required
            disabled={!isEditable}
          />
          <Input
            label="Email ID"
            name="coordEmail"
            register={register}
            errors={errors}
            required
            pattern={/^\S+@\S+$/}
            disabled={!isEditable}
          />
          <Input
            label="Contact Number"
            name="coordContact"
            register={register}
            errors={errors}
            required
            pattern={/^\d{10}$/}
            disabled={!isEditable}
          />
          <Input
            label="Alternate Contact (Optional)"
            name="coordAlt"
            register={register}
            errors={errors}
            pattern={/^\d{10}$/}
            disabled={!isEditable}
          />
        </div>
        <h4 className={styles.subheading}>
          B. <span className={styles.blue}> RESEARCH &</span>
          <span className={styles.orange}> INNOVATION INITIATIVES</span>
        </h4>
        <div className={styles.grid}>
          <Input
            label="Research Centers / Labs"
            name="labs"
            register={register}
            errors={errors}
            required
            disabled={!isEditable}
          />
          <Input
            label="Major Research Domains"
            name="researchDomains"
            register={register}
            errors={errors}
            required
            disabled={!isEditable}
          />
          <Input
            label="Innovation / Incubation Cell Details"
            name="incubationCell"
            register={register}
            errors={errors}
            required
            disabled={!isEditable}
          />
          <Input
            label="Flagship Annual Events"
            name="events"
            register={register}
            errors={errors}
            required
            disabled={!isEditable}
          />
        </div>
        <hr />
        {/* ------------ Section 3 ------------ */}
        <h2 className={styles.sectionTitle}>
          Part 3 — <span className={styles.blue}>ADDITIONAL INFORMATION </span>
          <br />
          <span className={styles.orange}>AND DOCUMENTS</span>
        </h2>
        <div className={styles.grid}>
          <Input
            label="Approx. No. of PG / PhD. Students"
            name="phdCount"
            register={register}
            errors={errors}
            required
            pattern={/^[0-9]+$/}
            disabled={!isEditable}
          />
          <Input
            label="Specific Goals or Expectations from IMPARC Membership"
            name="goals"
            register={register}
            errors={errors}
            required
            disabled={!isEditable}
          />
          <Input
            label="Any Additional Comments / Queries"
            name="comments"
            register={register}
            errors={errors}
            required
            disabled={!isEditable}
          />

          <FileUpload
            label="Supporting Media (Brochures / Reports / Photos)"
            name="supportingDocs"
            register={register}
            errors={errors}
            allowedTypes={["pdf", "jpg", "png", "jpeg"]}
            maxSizeMB={10}
            onFileSelect={(file) => console.log(file)}
            key={`supportingDocs-${fileUploadedKey}`}
            previewUrl={instituteProfile && instituteProfile.supportingDocs}
            disabled={!isEditable}
          />
        </div>
        <div className={styles.actions}>
          {isEditable && (
            <button className={styles.submit} type="submit">
              Update Profile
            </button>
          )}
        </div>
      </form>
    </>
  );
};
export default InsituteProfile;
